import {
  List,
  Datagrid,
  TextField,
  EditButton,
  CreateButton,
  FunctionField,
  ListProps,
  BulkDeleteButton,
  useListContext,
  useNotify,
  useRefresh,
  Button as RaButton,
} from 'react-admin';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import UserTransfer from './UserTransfer.tsx';

const UserListHeader = () => {
  const { total } = useListContext();

  return (
    <Paper elevation={0} sx={{ mb: 2, p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', mb: 0.5 }}>User Management</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Manage users, departments and role assignments.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Total Users: ${total ?? 0}`} sx={{ height: 32, fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }} />
          <CreateButton
            label="New User"
            sx={{
              px: 2,
              height: 34,
              borderRadius: '10px',
              fontWeight: 700,
              backgroundColor: '#0f172a',
              color: '#fff',
              '&:hover': { backgroundColor: '#111827' },
              '& .MuiButton-startIcon, & .MuiSvgIcon-root': { color: '#fff' },
            }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

const ActionButtons = ({ record }: { record?: any }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [transferOpen, setTransferOpen] = useState(false);

  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: '100%' }}>
      <EditButton record={record} sx={{ textTransform: 'capitalize', '& span': { marginRight: '2px' } }} />

      <RaButton
        label="Delete"
        onClick={(e: any) => { e.stopPropagation(); setTransferOpen(true); }}
        sx={{ textTransform: 'capitalize', color: '#dc2626' }}
      >
        <DeleteIcon />
      </RaButton>

      {transferOpen && (
        <UserTransfer
          open={transferOpen}
          fromUser={record}
          onClose={() => setTransferOpen(false)}
          onSuccess={() => { notify('Tasks transferred and user deactivated', { type: 'success' }); refresh(); }}
        />
      )}
    </Box>
  );
};



const UserList = (props: ListProps) => {
  const dataProvider = useDataProvider();
  const [departmentMap, setDepartmentMap] = useState<{ [key: string]: string }>({});
  const [roleMap, setRoleMap] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    // 拉取部门
    dataProvider.getList('permissionDepartment', {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'id', order: 'ASC' },
      filter: {}
    }).then(res => {
      const map: { [key: string]: string } = {};
      res.data.forEach((item: any) => {
        map[item.id] = item.departmentName;
      });
      setDepartmentMap(map);
    });
    // 拉取角色
    dataProvider.getList('permissionRoles', {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'id', order: 'ASC' },
      filter: {}
    }).then(res => {
      const map: { [key: string]: string } = {};
      res.data.forEach((item: any) => {
        map[item.id] = item.roleName;
      });
      setRoleMap(map);
    });
  }, [dataProvider]);

  return (
    <List
      sx={{ marginTop: '20px', px: 2.5, '& .RaList-content': { p: '20px !important' } }}
      empty={false}
      {...props} actions={false}
    >
      <UserListHeader />
      <Paper elevation={0} sx={{ p: 0.75, borderRadius: '14px', overflow: 'visible', border: '1px solid #e5e7eb', boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)', bgcolor: '#fff' }}>
        <Datagrid
          rowClick={false}
          isRowSelectable={() => true}
          bulkActionButtons={<BulkDeleteButton label="Batch Delete" />}
          sx={{
            borderRadius: '10px',
            overflow: 'hidden',
            '& .RaDatagrid-table': { tableLayout: 'fixed' },
            '& .RaDatagrid-headerCell': { textAlign: 'center', bgcolor: '#f8fafc', fontWeight: 700, color: '#334155', py: 1.25 },
            '& .RaDatagrid-rowCell': { textAlign: 'center', py: 1.1, verticalAlign: 'middle' },
            '& .RaDatagrid-headerCell:first-of-type, & .RaDatagrid-rowCell:first-of-type': { pl: 2 },
            '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': { pr: 1.5 },
            '& .RaDatagrid-headerCell.MuiTableCell-paddingCheckbox': { pr: '25px !important' },
            '& .RaDatagrid-row:nth-of-type(even)': { bgcolor: '#fbfdff' },
            '& .RaDatagrid-row:hover': { bgcolor: '#f8fafc' },
          }}
        >
          <FunctionField label="ID" render={(record: any) => <Chip size="small" label={`#${record?.id ?? '-'}`} sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700 }} />} />
          <FunctionField label="User Name" render={(record: any) => <Chip size="small" label={record?.userName || '-'} sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }} />} />
          <TextField sx={{ width: '320px', maxWidth: '320px', wordBreak: 'break-all' }} source="email" label="Mailbox" />
          <FunctionField label="Department" render={(record: any) => departmentMap[record.departmentId] || '-'} />
          <TextField source="departmentTile" label="Title" />
          <FunctionField label="Role" render={(record: any) => roleMap[record.roleId] || '-'} />
          <FunctionField
            label="Status"
            render={(record: any) =>
              record && record.isActive ? (
                <Chip label="Active" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
              ) : (
                <Chip label="Inactive" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700 }} />
              )
            }
          />
          <FunctionField label="Operations" render={(record: any) => <ActionButtons record={record} />} />
        </Datagrid>
      </Paper>
    </List>
  );
};

export default UserList;
